// VIS-6 (War Room 2026-09-21, marcado crítico) — ninguna vista del
// colaborador muestra un ID en lugar de un nombre.
//
// Por qué pasa: las fichas del padrón dadas de alta a mano (Europa, EE. UU.) nacen con un id propio,
// distinto del `oid` de Entra con el que la persona inicia sesión. Hasta el arreglo de backend del
// 2026-09-12 (el emisor firma con su `fichaId`), el reconocimiento se guardaba con el `oid` y un
// snapshot sin nombre: el propio id (antes del 2026-08-27) o vacío (del 27-ago al 12-sep). Ese
// snapshot queda CONGELADO en el documento, así que el muro seguía pintando el id o «Identidad no
// visible». Ver `docs/contexto/errores-conocidos.md` (snapshot roto, 2026-09-10).
//
// Qué se hace aquí: un nombre «roto» (vacío o igual al id) se intenta resolver EN VIVO contra el
// directorio; si tampoco se encuentra (un `oid` que ninguna ficha tiene), se muestra «Usuario no
// disponible». Mismo criterio que ya usaba la bandeja de Moderación (`nombreMostrable`).
//
// 🔴 Privacidad: un emisor ocultado A PROPÓSITO (anonimato, RN-V01, visibilidad por localidad) llega
// SIN `emisorId` — el backend borra id y nombre juntos (`RecognitionMapper.ToDto`). Por eso solo se
// repara cuando hay `emisorId`: nunca se busca en vivo a alguien que una regla decidió tapar.
import { useMemo } from 'react';
import type { RecognitionDto } from '@/api/recognition';
import { usePersonNameResolver } from '@/views/AnalyticsView/hooks/useAnalyticsNames';

/** Resolución en vivo por id; devuelve el mismo id cuando no lo encuentra (`usePersonNameResolver`). */
export type ResolverDeNombres = (id: string) => string;

/** Sin reparación en vivo: el id no se resuelve (lo que devuelve es el propio id). */
export const SIN_RESOLVER: ResolverDeNombres = (id) => id;

type ConNombres = Pick<RecognitionDto, 'anonimo' | 'emisorId' | 'emisorNombre' | 'receptorIds' | 'receptorNombres'>;

/** Un nombre que no se puede mostrar: vacío, solo espacios, o el propio identificador. */
export function esNombreRoto(nombre: string | null | undefined, id: string): boolean {
  return !nombre?.trim() || nombre === id;
}

/**
 * El nombre a pintar para `id`: el guardado si está sano; si no, el del directorio; si tampoco, el
 * `respaldo`. Nunca devuelve el id.
 */
export function nombreReparado(
  id: string,
  nombre: string | null | undefined,
  resolver: ResolverDeNombres,
  respaldo: string,
): string {
  if (!esNombreRoto(nombre, id)) return nombre as string;
  const enVivo = resolver(id);
  return esNombreRoto(enVivo, id) ? respaldo : enVivo;
}

/**
 * Cómo se emparejan `receptorIds` y `receptorNombres`. El backend arma los nombres desde los
 * snapshots, así que solo con el MISMO largo se sabe qué nombre es de qué id:
 *   · 'porIndice' — mismo largo: se repara cada posición.
 *   · 'sinNombres' — no vino ningún nombre (registro anterior a los snapshots): se resuelven todos.
 *   · 'descuadrado' — largos distintos: emparejar por posición atribuiría el nombre de uno a otro.
 *     Se muestran los nombres sanos y el respaldo por cada faltante, sin consultar a nadie.
 */
function emparejamiento(r: Pick<RecognitionDto, 'receptorIds' | 'receptorNombres'>): 'porIndice' | 'sinNombres' | 'descuadrado' {
  const { ids, nombres } = listasDe(r);
  if (nombres.length === ids.length) return 'porIndice';
  return nombres.length === 0 ? 'sinNombres' : 'descuadrado';
}

/** Las dos listas, vacías si no vinieron (el contrato las declara, pero un registro viejo o un
 *  doble de prueba puede omitirlas; recorrer `undefined` tumbaría la tarjeta entera). */
function listasDe(r: Pick<RecognitionDto, 'receptorIds' | 'receptorNombres'>) {
  return { ids: r.receptorIds ?? [], nombres: r.receptorNombres ?? [] };
}

/** Ids cuyo nombre guardado está roto: son los únicos que vale la pena pedir al directorio. */
export function idsConNombreRoto(reconocimientos: readonly ConNombres[]): string[] {
  const ids = new Set<string>();
  reconocimientos.forEach((r) => {
    if (!r.anonimo && r.emisorId && esNombreRoto(r.emisorNombre, r.emisorId)) ids.add(r.emisorId);
    if (emparejamiento(r) === 'descuadrado') return;
    const { ids: receptores, nombres } = listasDe(r);
    receptores.forEach((id, i) => {
      if (esNombreRoto(nombres[i], id)) ids.add(id);
    });
  });
  return [...ids].sort((a, b) => a.localeCompare(b));
}

/**
 * Resolver en vivo SOLO para los nombres rotos de `reconocimientos`. Con todos los nombres sanos (el
 * caso normal) no pide nada: `useUsersBatchLookup` no consulta con la lista vacía.
 */
export function useReparadorDeNombres(reconocimientos: readonly ConNombres[]): ResolverDeNombres {
  const ids = useMemo(() => idsConNombreRoto(reconocimientos), [reconocimientos]);
  return usePersonNameResolver(ids).resolvePersonName;
}

/** Nombres de los receptores, en orden, sin ids: cada uno reparado o con el respaldo. */
export function nombresDeReceptores(
  reconocimiento: Pick<RecognitionDto, 'receptorIds' | 'receptorNombres'>,
  resolver: ResolverDeNombres,
  respaldo: string,
): string[] {
  const { ids, nombres } = listasDe(reconocimiento);
  if (emparejamiento(reconocimiento) === 'descuadrado') {
    const sanos = nombres.filter((nombre) => !ids.some((id) => esNombreRoto(nombre, id)));
    const faltantes = Math.max(0, ids.length - sanos.length);
    return [...sanos, ...Array.from({ length: faltantes }, () => respaldo)];
  }
  return ids.map((id, i) => nombreReparado(id, nombres[i], resolver, respaldo));
}
